if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.onViewerKeydown = this.onViewerKeydown.bind(this);
        this.onNavButtonClick = this.onNavButtonClick.bind(this);
        this.onThumbnailKeydown = this.onThumbnailKeydown.bind(this);

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), 500)
          );
          this.elements.viewer.addEventListener('keydown', this.onViewerKeydown);
        }

        this.viewerNav = this.querySelector('.product-gallery__nav');
        if (this.viewerNav) {
          this.viewerNavButtons = this.viewerNav.querySelectorAll(
            'button[name="previous"], button[name="next"]'
          );
          this.viewerNavButtons.forEach((button) =>
            button.addEventListener('click', this.onNavButtonClick)
          );
        }

        this.updateNavigationState();

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;
          button.addEventListener(
            'click',
            this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
          );
          button.addEventListener('keydown', this.onThumbnailKeydown);
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches)
          this.removeListSemantic();
      }

      onSlideChanged(event) {
        const slides = this.mediaSlides();
        if (slides.length) {
          const targetElement = event?.detail?.currentElement;
          const currentIndex =
            targetElement && slides.includes(targetElement)
              ? slides.indexOf(targetElement)
              : this.currentSlideIndex(slides);
          this.updateNavigationState(currentIndex, slides.length);
        } else {
          this.updateNavigationState();
        }

        if (!this.elements.thumbnails || !event?.detail?.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onViewerKeydown(event) {
        if (!this.elements.viewer) return;
        const key = event.key;
        if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Home' && key !== 'End') return;

        const nextButton = this.elements.viewer.querySelector('button[name="next"]');
        const prevButton = this.elements.viewer.querySelector('button[name="previous"]');

        if (key === 'ArrowRight' && nextButton && !nextButton.hasAttribute('disabled')) {
          event.preventDefault();
          nextButton.click();
          return;
        }

        if (key === 'ArrowLeft' && prevButton && !prevButton.hasAttribute('disabled')) {
          event.preventDefault();
          prevButton.click();
          return;
        }

        if (key === 'Home') {
          event.preventDefault();
          this.jumpToIndex(0);
        } else if (key === 'End') {
          event.preventDefault();
          this.jumpToIndex(-1);
        }
      }

      jumpToIndex(position) {
        if (!this.elements.viewer) return;
        const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        if (!slides.length) return;
        const target = position === -1 ? slides[slides.length - 1] : slides[Math.max(0, position)];
        if (!target) return;
        const mediaId = target.dataset.mediaId || target.getAttribute('data-media-id');
        if (!mediaId) return;
        this.setActiveMedia(mediaId, false);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        // Update active media in the main viewer
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);
        const slides = this.mediaSlides();

        // ── FIX: keep navigation state in sync with the *new* active thumbnail ──
        if (!this.elements.thumbnails) {
          // No thumbnails: just use slide index
          this.updateNavigationState(slides.indexOf(activeMedia), slides.length);
          return;
        }

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );

        if (activeThumbnail) {
          // First update aria-current on thumbnails…
          this.setActiveThumbnail(activeThumbnail);
          // …then compute the current index from the updated aria-current
          const buttons = this.getThumbnailButtons();
          const currentIndex = this.currentThumbnailIndex(buttons);
          this.updateNavigationState(currentIndex, buttons.length);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        } else {
          // Fallback: still keep arrows/counter usable even if no matching thumb
          this.updateNavigationState(slides.indexOf(activeMedia), slides.length);
        }
      }

      mediaSlides() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
      }

      currentSlideIndex(slides = this.mediaSlides()) {
        return slides.findIndex((element) => element.classList?.contains('is-active'));
      }

      updateNavigationState(currentIndex = null, total = null) {
        if (!this.viewerNav || !this.shouldHandleNavigation()) return;
        let totalSlides = total;
        let resolvedIndex = currentIndex;

        if (this.elements.thumbnails) {
          const thumbnailButtons = this.getThumbnailButtons();
          totalSlides = thumbnailButtons.length;
          if (totalSlides) {
            const currentThumbnailIndex = this.currentThumbnailIndex(thumbnailButtons);
            if (resolvedIndex === null || resolvedIndex === undefined || resolvedIndex < 0) {
              resolvedIndex = currentThumbnailIndex;
            }
          }
        }

        if (totalSlides === null || totalSlides === undefined) {
          const slides = this.mediaSlides();
          totalSlides = slides.length;
          if (resolvedIndex === null || resolvedIndex === undefined || resolvedIndex < 0) {
            resolvedIndex = this.currentSlideIndex(slides);
          }
        }

        if (!totalSlides) {
          this.toggleNavButtonState('previous', true);
          this.toggleNavButtonState('next', true);
          this.updateCounter(0, 0);
          return;
        }

        const safeIndex =
          resolvedIndex === -1 || resolvedIndex === null || resolvedIndex === undefined
            ? 0
            : Math.min(Math.max(resolvedIndex, 0), totalSlides - 1);

        this.toggleNavButtonState('previous', safeIndex <= 0);
        this.toggleNavButtonState('next', safeIndex >= totalSlides - 1);
        this.updateCounter(safeIndex + 1, totalSlides);
      }

      toggleNavButtonState(name, shouldDisable) {
        if (!this.viewerNav) return;
        const button = this.viewerNav.querySelector(`button[name="${name}"]`);
        if (!button) return;
        if (shouldDisable) {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
        }
      }

      updateCounter(current, total) {
        if (!this.viewerNav) return;
        const currentEl = this.viewerNav.querySelector('.slider-counter--current');
        if (currentEl) {
          const normalizedCurrent = total < 1 ? 0 : Math.min(Math.max(current, 1), total);
          currentEl.textContent = normalizedCurrent;
        }
        const totalEl = this.viewerNav.querySelector('.slider-counter--total');
        if (totalEl) totalEl.textContent = total;
      }

      shouldHandleNavigation() {
        if (!this.viewerNav || !this.elements.viewer) return false;
        const layout = this.dataset.desktopLayout || '';
        if (layout.includes('thumbnail')) return true;
        if (!this.elements.viewer.sliderItemsToShow) return false;
        return this.elements.viewer.sliderItemsToShow.length < 2;
      }

      onNavButtonClick(event) {
        if (!this.shouldHandleNavigation()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const step = event.currentTarget.name === 'next' ? 1 : -1;
        this.navigateByStep(step);
      }

      navigateSlides(step) {
        const slides = this.mediaSlides();
        if (!slides.length) return;
        const currentIndex = this.currentSlideIndex(slides);
        const targetIndex = Math.min(slides.length - 1, Math.max(0, currentIndex + step));
        if (targetIndex === currentIndex || targetIndex < 0) return;
        const targetSlide = slides[targetIndex];
        const mediaId = targetSlide?.dataset?.mediaId;
        if (!mediaId) return;
        this.setActiveMedia(mediaId, false);
      }

      navigateByStep(step, baseIndex = null) {
        if (this.elements.thumbnails) {
          const buttons = this.getThumbnailButtons();
          if (!buttons.length) return;
          const startIndex =
            baseIndex !== null && baseIndex !== undefined
              ? baseIndex
              : this.currentThumbnailIndex(buttons);
          const targetIndex = Math.min(buttons.length - 1, Math.max(0, startIndex + step));
          if (targetIndex === startIndex) {
            this.updateNavigationState(targetIndex, buttons.length);
            return;
          }
          this.activateThumbnailIndex(targetIndex, buttons);
          return;
        }
        this.navigateSlides(step);
      }

      getThumbnailButtons() {
        if (!this.elements.thumbnails) return [];
        return Array.from(this.elements.thumbnails.querySelectorAll('[data-target] button'));
      }

      currentThumbnailIndex(buttons = this.getThumbnailButtons()) {
        if (!buttons.length) return 0;
        const index = buttons.findIndex((button) => button.hasAttribute('aria-current'));
        return index === -1 ? 0 : index;
      }

      activateThumbnailIndex(index, buttons = this.getThumbnailButtons()) {
        if (!buttons.length) return;
        const clampedIndex = Math.min(Math.max(index, 0), buttons.length - 1);
        const button = buttons[clampedIndex];
        if (!button) return;
        button.focus();
        button.click();
      }

      onThumbnailKeydown(event) {
        if (!this.shouldHandleNavigation()) return;
        const key = event.key;
        const buttons = this.getThumbnailButtons();
        if (!buttons.length) return;
        const currentIndex = buttons.indexOf(event.currentTarget);
        if (currentIndex === -1) return;

        if (key === 'ArrowRight' || key === 'ArrowDown') {
          event.preventDefault();
          this.navigateByStep(1, currentIndex);
        } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
          event.preventDefault();
          this.navigateByStep(-1, currentIndex);
        } else if (key === 'Home') {
          event.preventDefault();
          this.activateThumbnailIndex(0, buttons);
        } else if (key === 'End') {
          event.preventDefault();
          this.activateThumbnailIndex(buttons.length - 1, buttons);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );
}
